/* eslint-disable @typescript-eslint/no-use-before-define */

import { useMutation } from '@apollo/react-hooks';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { loader } from 'graphql.macro';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import { useAnalytics } from '@modernhealth/web-analytics';
import { colors } from '@modernhealth/vital-foundation';

import backIcon from 'src/public/img/arrow-back-circle.svg';
import closeIcon from 'src/public/img/icon-close-circle.svg';

import { config } from 'src/config/config';
import { toastNotificationsService } from 'src/config/rootService';
import { AccountProviderSchedulingType } from 'src/generated/webapp_gql';
import { isProviderAssignmentStillActive } from 'src/modules/providers/selectors/providersSelectors';
import { ProviderType } from 'src/modules/user/models/user';
import { delay } from 'src/shared/helpers/delay';
import { useWindowSize } from 'src/shared/hooks/useWindowSize';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { RateSessionBeforeYouLeaveStep } from '../components/RateSessionBeforeYouLeaveStep';
import { RateSessionIntroStep } from '../components/RateSessionIntroStep';
import { RateSessionNPSQuestionStep } from '../components/RateSessionNPSQuestionStep';
import { RateSessionRangeQuestion } from '../components/RateSessionRangeQuestionStep';
import { RateSessionStepWrapper } from '../components/RateSessionStepWrapper';
import { RateSessionTextFeedbackStep } from '../components/RateSessionTextFeedbackStep';
import { RateSessionThankYouStep } from '../components/RateSessionThankYouStep';
import { RateSessionTileQuestion } from '../components/RateSessionTileQuestionStep';
import { SessionLowRatingStep } from '../components/SessionLowRatingStep';
import { useShouldShowNpsQuestionInSessionRatingLazyQuery } from '../documents/shouldShowNpsQuestionInSessionRating.generated';
import {
    answersOptionsPamm,
    constructiveAnswersOptions,
    positiveAnswersOptions,
} from '../models/videoSessions';
import { getSessionUtilizationInfo, getVideoSession } from '../selectors/videoSessionsSelectors';
import { RateSessionMCQuestion } from '../components/RateSessionMCQuestionStep';
import type { RootState } from 'src/config/rootStore';
import { captureException } from 'src/shared/helpers/captureException';

const CREATE_REVIEW = loader('../documents/createCoachingVisitMemberReview.graphql');
const CREATE_PSYCH_REVIEW = loader('../documents/createPsychiatryVisitMemberReview.graphql');

interface OuterWrapperProps {
    activeStep: FeedbackStep;
    isPsychiatry: boolean;
}

const OuterWrapper = styled.div<OuterWrapperProps>(props => ({
    backgroundColor:
        props.activeStep === (props.isPsychiatry ? PammStep.ThankYouStep : Step.ThankYouStep)
            ? colors.blue75
            : colors.white,
    overflow: 'hidden',
    display: 'flex',
    flex: 1,
    width: '100%',
}));

interface WrapperProps {
    width: number;
}

const Wrapper = styled.div<WrapperProps>(props => ({
    maxWidth: 1440,
    width: props.width,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    margin: '0 auto',
}));

// for initial measurement of window width
const ContentPlaceholder = styled.div({
    height: 1,
    width: '100vw',
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '36px 32px 0',
    zIndex: 5,
});

const ButtonImage = styled.img({
    width: 49,
    height: 49,
});

interface StepsWrapperProps {
    stepIndex: number;
}

const StepsWrapper = styled.div<StepsWrapperProps>(({ stepIndex }) => ({
    display: 'flex',
    flex: 1,
    zIndex: 1,
    flexDirection: 'row',
    maxWidth: 1440,
    width: '100%',
    marginTop: -100, // height of buttons row
    transition: 'transform .5s ease-in-out',
    transform: `translateX(-${stepIndex * 100}%)`,
}));

export enum Step {
    IntroStep,
    PositiveFeedbackStep,
    ConstructiveFeedbackStep,
    FirstOutcomeQuestion,
    SecondOutcomeQuestion,
    ThirdOutcomeQuestion,
    TextFeedbackStep,
    NPSQuestionStep,
    ThankYouStep,
    LowRatingStep,
    BeforeYouLeaveStep,
}

export enum PammStep {
    IntroStep,
    PositiveFeedbackStep,
    ConstructiveFeedbackStep,
    FirstOutcomeQuestion,
    SecondOutcomeQuestion,
    ThirdOutcomeQuestion,
    PlanToContinueQuestion,
    UnderstandRecommendationQuestion,
    TextFeedbackStep,
    NPSQuestionStep,
    ThankYouStep,
    BeforeYouLeaveStep,
}

export const exitedAtStepValue = {
    [Step.IntroStep]: 'RATING',
    [Step.PositiveFeedbackStep]: 'WHAT_YOU_LIKED_ABOUT_YOUR_COACH',
    [Step.ConstructiveFeedbackStep]: 'WHAT_COULD_HAVE_BEEN_BETTER',
    [Step.FirstOutcomeQuestion]: 'ABILITY_TO_HELP_ME',
    [Step.SecondOutcomeQuestion]: 'WORKED_ON_AGREED_UPON_GOALS',
    [Step.ThirdOutcomeQuestion]: 'IMPROVED_MY_WELL_BEING',
    [Step.TextFeedbackStep]: 'FEEDBACK',
};

export const exitedAtStepValuePamm = {
    [PammStep.IntroStep]: 'RATING',
    [PammStep.PositiveFeedbackStep]: 'WHAT_YOU_LIKED',
    [PammStep.ConstructiveFeedbackStep]: 'WHAT_COULD_HAVE_BEEN_BETTER',
    [PammStep.FirstOutcomeQuestion]: 'ABILITY_TO_HELP_ME',
    [PammStep.SecondOutcomeQuestion]: 'UNDERSTANDING_OF_TREATMENT_PLAN',
    [PammStep.ThirdOutcomeQuestion]: 'IMPROVED_MY_WELL_BEING',
    [PammStep.PlanToContinueQuestion]: 'WILL_CONTINUE_WORKING',
    [PammStep.UnderstandRecommendationQuestion]: 'UNDERSTOOD_RECOMMENDATIONS',
    [PammStep.TextFeedbackStep]: 'FEEDBACK',
};

export type FeedbackStep = Step | PammStep;

export enum RateVideoSessionPageTestID {
    BackButton = 'RateVideoSessionPageTestID.BackButton',
    CloseButton = 'RateVideoSessionPageTestID.CloseButton',
}

interface ButtonProps {
    visible: boolean;
}

const Button = styled.button<ButtonProps>(props => ({
    border: 'none',
    background: 'none',
    padding: 8,
    opacity: props.visible ? 1 : 0,
    zIndex: props.visible ? 5 : -1,
    pointerEvents: props.visible ? 'auto' : 'none',
    transition: 'opacity .4s ease-in-out',
}));

const stepsWithoutBackButton: FeedbackStep[] = [
    Step.IntroStep,
    Step.NPSQuestionStep,
    Step.ThankYouStep,
    Step.LowRatingStep,
];
const stepsWithoutCloseButton: FeedbackStep[] = [
    Step.IntroStep,
    Step.BeforeYouLeaveStep,
    Step.NPSQuestionStep,
    Step.ThankYouStep,
];

const recommendationsChoices = {
    'session_rating_modal.text.understanding.start_medication': 'Start a new medication',
    'session_rating_modal.text.understanding.stop_medication': 'Stop taking a medication',
    'session_rating_modal.text.understanding.change_medication':
        'Stay on my current medications, but change the dose, frequency, and/or duration of one or more medications',
    'session_rating_modal.text.understanding.no_change':
        'Stay on my current medication regimen with no changes',
    'session_rating_modal.text.understanding.other_provider':
        'Meet with a psychiatry and medication management provider outside of Modern Health.',
    'session_rating_modal.text.understanding.further_assessment':
        'Get further assessment or evaluation related to psychiatry and medication management',
};

export const RateVideoSessionPage: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation<{ session?: string }>();
    const size = useWindowSize();
    const flags = useFlags();
    const analytics = useAnalytics();

    const sessionUuid = location?.state?.session;
    const session = useSelector(getVideoSession(sessionUuid));
    const { careRecommendations } = useSelector(getUserInformation);

    const provider = session?.coachAssignment?.coach;
    const isPsychiatrySession = provider?.providerType === ProviderType.PSYCHIATRIST;

    const [activeStep, setActiveStep] = useState<FeedbackStep>(Step.IntroStep);
    const [exitedAtStep, setExitedAtStep] = useState<FeedbackStep | null>(null);
    const [steps, setSteps] = useState<FeedbackStep[]>(
        isPsychiatrySession
            ? [
                  PammStep.IntroStep,
                  PammStep.NPSQuestionStep,
                  PammStep.ThankYouStep,
                  PammStep.BeforeYouLeaveStep,
              ]
            : [Step.IntroStep, Step.NPSQuestionStep, Step.ThankYouStep, Step.BeforeYouLeaveStep],
    );
    const [rating, setRating] = useState(0);

    const { coachingVisitsRemaining, therapyVisitsRemaining } =
        useSelector(getSessionUtilizationInfo);
    const providerAssignmentIsActive = useSelector<RootState, boolean>(state =>
        provider?.uuid ? isProviderAssignmentStillActive(state, provider.uuid) : false,
    );

    const [positiveAnswers, setPositiveAnswers] = useState([]);
    const [constructiveAnswers, setConstructiveAnswers] = useState([]);
    const [pammAnswer, setPammAnswer] = useState('');

    const [outcomesAnswers, setOutcomesAnswers] = useState<(null | number)[]>([null, null, null]);
    const [textFeedback, setTextFeedback] = useState('');

    const [understoodAnswer, setUnderstoodAnswer] = useState('');

    const [fetchNpsQuestionInfo, { data }] = useShouldShowNpsQuestionInSessionRatingLazyQuery({
        fetchPolicy: 'network-only',
        variables: { coachingVisitUuid: sessionUuid },
    });

    const [createReview, { loading }] = useMutation(CREATE_REVIEW);

    const [createPsychiatryReview, { loading: pammLoading }] = useMutation(CREATE_PSYCH_REVIEW);

    const currentStepIndex = steps.indexOf(activeStep);

    const goToHomePage = () => history.replace('/');

    const getStepsInRightOrder = (ratingValue: number): FeedbackStep[] => {
        const goodStepFirst = [Step.PositiveFeedbackStep, Step.ConstructiveFeedbackStep];
        const badStepFirst = [Step.ConstructiveFeedbackStep, Step.PositiveFeedbackStep];

        const goodStepFirstPamm = [
            PammStep.PositiveFeedbackStep,
            PammStep.ConstructiveFeedbackStep,
        ];
        const badStepFirstPamm = [PammStep.ConstructiveFeedbackStep, PammStep.PositiveFeedbackStep];

        return isPsychiatrySession
            ? [
                  PammStep.IntroStep,
                  ...(ratingValue > 3 ? goodStepFirstPamm : badStepFirstPamm),
                  PammStep.FirstOutcomeQuestion,
                  PammStep.SecondOutcomeQuestion,
                  PammStep.ThirdOutcomeQuestion,
                  PammStep.PlanToContinueQuestion,
                  PammStep.UnderstandRecommendationQuestion,
                  PammStep.TextFeedbackStep,
                  PammStep.NPSQuestionStep,
                  PammStep.ThankYouStep,
                  PammStep.BeforeYouLeaveStep,
              ]
            : [
                  Step.IntroStep,
                  ...(ratingValue > 3 ? goodStepFirst : badStepFirst),
                  Step.FirstOutcomeQuestion,
                  Step.SecondOutcomeQuestion,
                  Step.ThirdOutcomeQuestion,
                  Step.TextFeedbackStep,
                  Step.NPSQuestionStep,
                  Step.ThankYouStep,
                  Step.LowRatingStep,
                  Step.BeforeYouLeaveStep,
              ];
    };

    const pammMCQuestionOptions = [
        ...Object.keys(recommendationsChoices).map(choice => {
            return { label: t(`videoSessions:${choice}`), value: recommendationsChoices[choice] };
        }),
        {
            label: t('translation:connectToAnExpert.selectedTopicsScreen.otherCategory'),
            value: 'Other',
        },
    ];

    const answersOptionsPammContinueWorking = [
        { label: t('providers:demographic_feedback_modal.answer.yes'), value: 'Yes' },
        { label: t('providers:demographic_feedback_modal.answer.no'), value: 'No' },
    ];

    useEffect(() => {
        if (!session || !provider) {
            Sentry.withScope(scope => {
                scope.setExtra('session', session);
                scope.setExtra('provider', provider);

                Sentry.captureException(
                    new Error('[Session Rating Page] Could not find session object'),
                );
            });
            captureException(new Error('[Session Rating Page] Could not find session object'), {
                provider,
                session,
            });
            goToHomePage();
        } else {
            fetchNpsQuestionInfo();
        }
    }, [session, provider]);

    useEffect(() => {
        const exitedAtStepToUse = isPsychiatrySession ? exitedAtStepValuePamm : exitedAtStepValue;
        if (!exitedAtStepToUse.hasOwnProperty(activeStep)) return;

        analytics.trackEvent(
            EventLexicon.singlePageFlowVideoSessionRatingQuestions({
                providerType: provider?.providerType ?? '',
                providerSubtype: provider?.psychiatristSubtype ?? '',
                providerUuid: provider?.uuid,
                careRecommendation: careRecommendations[0]?.clinicalRecommendation ?? '',
                modalPageName: exitedAtStepToUse[activeStep],
            }),
        );
    }, [activeStep, analytics, careRecommendations, isPsychiatrySession, provider]);

    const shouldShowNpsQuestion =
        (!flags.hideNpsInSessionRating && data?.shouldShowNpsQuestion) || isPsychiatrySession;

    const hideBackButton =
        currentStepIndex === 1 ||
        (!isPsychiatrySession && stepsWithoutBackButton.includes(activeStep)) ||
        (isPsychiatrySession &&
            (activeStep === PammStep.IntroStep || activeStep === PammStep.ThankYouStep));
    const hideCloseButton = !isPsychiatrySession && stepsWithoutCloseButton.includes(activeStep);

    const hasMoreSessions =
        (provider?.providerType === ProviderType.COACH
            ? coachingVisitsRemaining
            : therapyVisitsRemaining) > 0;

    // user has skipped stars rating or selected a 'good' amount of stars
    const ratingQualifies = rating === 0 || rating > 3;

    const shouldShowScheduling =
        ratingQualifies &&
        hasMoreSessions &&
        provider?.isInAppProvider &&
        provider?.providerSchedulingType !== AccountProviderSchedulingType.Roster &&
        providerAssignmentIsActive;

    if (!session || !provider) {
        return null;
    }

    const goToScheduleNextSession = (leavingFlow?: boolean) => {
        if (shouldShowScheduling) {
            return history.replace('/schedule-session/calendar', {
                coachUuid: provider?.uuid,
                disableBack: true,
            });
        } else if (rating > 0 && rating <= 3 && providerAssignmentIsActive && !leavingFlow) {
            setActiveStep(Step.LowRatingStep);
        } else {
            goToHomePage();
        }
    };

    const goToTheNextStep = () => {
        const nextStep = steps[currentStepIndex + 1];

        if (nextStep) {
            setActiveStep(nextStep);
        }
    };

    const goToThePreviousStep = () => {
        if (
            (activeStep === Step.BeforeYouLeaveStep || activeStep === Step.NPSQuestionStep) &&
            exitedAtStep &&
            !isPsychiatrySession
        ) {
            setActiveStep(exitedAtStep);
            setExitedAtStep(null);
            return;
        }

        if (
            (activeStep === PammStep.BeforeYouLeaveStep ||
                activeStep === PammStep.NPSQuestionStep) &&
            exitedAtStep &&
            isPsychiatrySession
        ) {
            setActiveStep(exitedAtStep);
            setExitedAtStep(null);
            return;
        }

        if (currentStepIndex < 1) {
            return;
        }

        const previousStep = steps[currentStepIndex - 1];
        setActiveStep(previousStep);
    };

    const handleIntroStepSkipPress = (leavingFlow?: boolean) => {
        if (!isPsychiatrySession) {
            createReview({
                variables: {
                    coachingVisitUuid: sessionUuid,
                    exitedAt: exitedAtStepValue[exitedAtStep ? exitedAtStep : Step.IntroStep],
                },
            }).catch(error => {
                Sentry.captureException(error);
                captureException(error);
            });
        } else {
            createPsychiatryReview({
                variables: {
                    coachingVisitUuid: sessionUuid,
                    exitedAt:
                        exitedAtStepValuePamm[exitedAtStep ? exitedAtStep : PammStep.IntroStep],
                },
            });
        }

        if (shouldShowNpsQuestion) {
            return setActiveStep(
                isPsychiatrySession ? PammStep.NPSQuestionStep : Step.NPSQuestionStep,
            );
        } else {
            goToScheduleNextSession(leavingFlow);
        }
    };

    const navigateToThankYouScreen = async () => {
        setActiveStep(isPsychiatrySession ? PammStep.ThankYouStep : Step.ThankYouStep);

        // lottie success animation duration
        await delay(3000);

        !isPsychiatrySession && goToScheduleNextSession();
    };

    const handleNpsQuestionSkip = () => {
        // check if user completed the first part of the flow
        if (rating !== 0) {
            navigateToThankYouScreen();
        } else {
            if (!isPsychiatrySession) {
                goToScheduleNextSession();
            } else {
                goToHomePage();
            }
        }
    };

    const handleNotNowPress = () => {
        handleIntroStepSkipPress(true);
    };

    const handleCloseButtonPress = () => {
        if (hideCloseButton) {
            return;
        }

        if (isPsychiatrySession && activeStep === PammStep.ThankYouStep) {
            return goToHomePage();
        }

        if (!isPsychiatrySession && activeStep === Step.LowRatingStep) {
            return goToHomePage();
        }

        // show before you leave modal
        setActiveStep(currentStep => {
            setExitedAtStep(currentStep);

            return isPsychiatrySession ? PammStep.BeforeYouLeaveStep : Step.BeforeYouLeaveStep;
        });
    };

    const handleRatingChange = (newRating: number) => {
        setRating(currentRating => {
            if (currentRating === 0) {
                const stepsInRightOrder = getStepsInRightOrder(newRating);

                setSteps(stepsInRightOrder);
                setActiveStep(stepsInRightOrder[1]);
            }

            return newRating;
        });
    };

    const handleTileAnswerClick =
        (answers: string[], setAnswersFunc: Dispatch<SetStateAction<string[]>>) =>
        (answer: string) => {
            const alreadySelected = answers.includes(answer);

            const newAnswers = alreadySelected
                ? answers.filter(val => val !== answer)
                : [...answers, answer];

            setAnswersFunc(newAnswers);
        };

    const handleRangeAnswerClick = (index: number) => async (answer: number) => {
        const newAnswers = [...outcomesAnswers];
        newAnswers[index] = answer;
        setOutcomesAnswers(newAnswers);

        await delay(200);

        goToTheNextStep();
    };

    const handleSubmit = async () => {
        if (loading || pammLoading) {
            return;
        }

        try {
            if (!isPsychiatrySession) {
                const response = await createReview({
                    variables: {
                        coachingVisitUuid: sessionUuid,
                        rating,
                        feedbackText: textFeedback,
                        whatYouLikedAboutYourCoach: positiveAnswers,
                        whatCouldHaveBeenBetter: constructiveAnswers,
                        abilityToHelpMe: outcomesAnswers[0],
                        workedOnAgreedUponGoals: outcomesAnswers[1],
                        improvedMyWellBeing: outcomesAnswers[2],
                        exitedAt: exitedAtStep ? exitedAtStepValue[exitedAtStep] : null,
                    },
                });

                const ok = get(response, 'data.createCoachingVisitMemberReview.ok');

                if (!ok) {
                    throw new Error('Received non ok response');
                }
            } else {
                const response = await createPsychiatryReview({
                    variables: {
                        coachingVisitUuid: sessionUuid,
                        rating,
                        feedbackText: textFeedback,
                        whatYouLiked: positiveAnswers,
                        whatCouldHaveBeenBetter: constructiveAnswers,
                        abilityToHelpMe: outcomesAnswers[0],
                        understandingOfTreatmentPlan: outcomesAnswers[1],
                        improvedMyWellBeing: outcomesAnswers[2],
                        willContinueWorking: pammAnswer === 'Yes',
                        understoodRecommendations: understoodAnswer,
                        exitedAt: exitedAtStep ? exitedAtStepValuePamm[exitedAtStep] : null,
                    },
                });

                const ok = get(response, 'data.createPsychiatryVisitMemberReview.ok');

                if (!ok) {
                    throw new Error('Received non ok response');
                }
            }
        } catch (error) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);

            Sentry.withScope(scope => {
                scope.setExtra('sessionUuid', sessionUuid);
                scope.setExtra('error_info', 'Error: failed to submit a session rating');

                Sentry.captureException(error);
            });
            captureException(error, {
                sessionUuid,
                error_info: 'Error: failed to submit a session rating.',
            });
        }

        if (shouldShowNpsQuestion) {
            return setActiveStep(
                isPsychiatrySession ? PammStep.NPSQuestionStep : Step.NPSQuestionStep,
            );
        }

        navigateToThankYouScreen();
    };

    const skipFirstPammQuestion = () => {
        setPammAnswer('');
        goToTheNextStep();
    };

    const skipUnderstoodQuestion = () => {
        setUnderstoodAnswer('');
        goToTheNextStep();
    };

    const skipFeedbackQuestion = () => {
        setTextFeedback('');
        handleSubmit();
    };

    const rateSessionStepWrapperProps = {
        width: size.width,
        activeStep,
    };

    // to prevent UI elements from weirdly jumping around on initial render
    if (size.width === undefined) {
        return <ContentPlaceholder />;
    }

    const stepsRenderersCat = {
        [Step.IntroStep]: () => (
            <RateSessionStepWrapper {...rateSessionStepWrapperProps} visibleFor={Step.IntroStep}>
                <RateSessionIntroStep
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onSkipClick={handleIntroStepSkipPress}
                />
            </RateSessionStepWrapper>
        ),
        [Step.PositiveFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.PositiveFeedbackStep}
            >
                <RateSessionTileQuestion
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onContinueClick={goToTheNextStep}
                    onAnswerClick={handleTileAnswerClick(positiveAnswers, setPositiveAnswers)}
                    question={t('videoSessions:session_rating_modal.positive_feedback_question')}
                    answersOptions={positiveAnswersOptions}
                    selectedAnswers={positiveAnswers}
                />
            </RateSessionStepWrapper>
        ),
        [Step.ConstructiveFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.ConstructiveFeedbackStep}
            >
                <RateSessionTileQuestion
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onContinueClick={goToTheNextStep}
                    onAnswerClick={handleTileAnswerClick(
                        constructiveAnswers,
                        setConstructiveAnswers,
                    )}
                    question={t(
                        'videoSessions:session_rating_modal.constructive_feedback_question',
                    )}
                    answersOptions={constructiveAnswersOptions}
                    selectedAnswers={constructiveAnswers}
                />
            </RateSessionStepWrapper>
        ),
        [Step.FirstOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.FirstOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t('videoSessions:session_rating_modal.first_outcome_question', {
                        name: provider.firstName,
                    })}
                    selectedAnswer={outcomesAnswers[0]}
                    onAnswerClick={handleRangeAnswerClick(0)}
                />
            </RateSessionStepWrapper>
        ),
        [Step.SecondOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.SecondOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t('videoSessions:session_rating_modal.second_outcome_question', {
                        name: provider.firstName,
                    })}
                    selectedAnswer={outcomesAnswers[1]}
                    onAnswerClick={handleRangeAnswerClick(1)}
                />
            </RateSessionStepWrapper>
        ),
        [Step.ThirdOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.ThirdOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t(
                        `videoSessions:session_rating_modal.third_outcome_question.${
                            provider.providerType === ProviderType.COACH ? 'coaching' : 'therapy'
                        }`,
                    )}
                    selectedAnswer={outcomesAnswers[2]}
                    onAnswerClick={handleRangeAnswerClick(2)}
                />
            </RateSessionStepWrapper>
        ),
        [Step.TextFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.TextFeedbackStep}
            >
                <RateSessionTextFeedbackStep
                    onContinueClick={handleSubmit}
                    textValue={textFeedback}
                    onTextChange={setTextFeedback}
                    loading={loading}
                />
            </RateSessionStepWrapper>
        ),
        [Step.NPSQuestionStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={Step.NPSQuestionStep}
                innerWrapperMaxWidth={570}
            >
                <RateSessionNPSQuestionStep
                    onSkip={handleNpsQuestionSkip}
                    onSubmit={navigateToThankYouScreen}
                    sessionUuid={sessionUuid || ''}
                />
            </RateSessionStepWrapper>
        ),
    };

    const stepsRenderersPamm = {
        [PammStep.IntroStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.IntroStep}
            >
                <RateSessionIntroStep
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onSkipClick={handleIntroStepSkipPress}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.PositiveFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.PositiveFeedbackStep}
            >
                <RateSessionTileQuestion
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onContinueClick={goToTheNextStep}
                    onAnswerClick={handleTileAnswerClick(positiveAnswers, setPositiveAnswers)}
                    question={t('videoSessions:session_rating_modal.positive_feedback_question')}
                    answersOptions={answersOptionsPamm}
                    selectedAnswers={positiveAnswers}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.ConstructiveFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.ConstructiveFeedbackStep}
            >
                <RateSessionTileQuestion
                    provider={provider}
                    rating={rating}
                    onRatingChange={handleRatingChange}
                    onContinueClick={goToTheNextStep}
                    onAnswerClick={handleTileAnswerClick(
                        constructiveAnswers,
                        setConstructiveAnswers,
                    )}
                    question={t(
                        'videoSessions:session_rating_modal.constructive_feedback_question',
                    )}
                    answersOptions={answersOptionsPamm}
                    selectedAnswers={constructiveAnswers}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.FirstOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.FirstOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t(
                        'videoSessions:session_rating_modal.first_outcome_question.psychiatry',
                        {
                            name: `${provider.firstName} ${provider.lastName}`,
                        },
                    )}
                    selectedAnswer={outcomesAnswers[0]}
                    onAnswerClick={handleRangeAnswerClick(0)}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.SecondOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.SecondOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t('videoSessions:session_rating_modal.text.good_understanding')}
                    selectedAnswer={outcomesAnswers[1]}
                    onAnswerClick={handleRangeAnswerClick(1)}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.ThirdOutcomeQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.ThirdOutcomeQuestion}
            >
                <RateSessionRangeQuestion
                    question={t(
                        'videoSessions:session_rating_modal.text.psychiatry_improved_well_being',
                    )}
                    selectedAnswer={outcomesAnswers[2]}
                    onAnswerClick={handleRangeAnswerClick(2)}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.PlanToContinueQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.PlanToContinueQuestion}
            >
                <RateSessionMCQuestion
                    onContinueClick={goToTheNextStep}
                    onSkipClick={skipFirstPammQuestion}
                    question={t('videoSessions:session_rating_modal.text.continue_working')}
                    answersOptions={answersOptionsPammContinueWorking}
                    onAnswerClick={(answer: string) => {
                        setPammAnswer(answer);
                    }}
                    isDisabled={pammAnswer === ''}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.UnderstandRecommendationQuestion]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.UnderstandRecommendationQuestion}
            >
                <RateSessionMCQuestion
                    onContinueClick={goToTheNextStep}
                    onSkipClick={skipUnderstoodQuestion}
                    question={t('videoSessions:session_rating_modal.text.understanding')}
                    answersOptions={pammMCQuestionOptions}
                    onAnswerClick={(answer: string) => {
                        setUnderstoodAnswer(answer);
                    }}
                    isDisabled={understoodAnswer === ''}
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.TextFeedbackStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.TextFeedbackStep}
            >
                <RateSessionTextFeedbackStep
                    onContinueClick={handleSubmit}
                    onSkipClick={skipFeedbackQuestion}
                    textValue={textFeedback}
                    onTextChange={setTextFeedback}
                    loading={loading}
                    isPamm
                />
            </RateSessionStepWrapper>
        ),
        [PammStep.NPSQuestionStep]: () => (
            <RateSessionStepWrapper
                {...rateSessionStepWrapperProps}
                visibleFor={PammStep.NPSQuestionStep}
                innerWrapperMaxWidth={570}
            >
                <RateSessionNPSQuestionStep
                    onSkip={handleNpsQuestionSkip}
                    onSubmit={navigateToThankYouScreen}
                    sessionUuid={sessionUuid || ''}
                />
            </RateSessionStepWrapper>
        ),
    };

    const shouldUseStepsRenderers = () => {
        if (!isPsychiatrySession) {
            return ![Step.BeforeYouLeaveStep, Step.ThankYouStep, Step.LowRatingStep].includes(
                activeStep as Step,
            );
        }
        return ![PammStep.BeforeYouLeaveStep, PammStep.ThankYouStep].includes(
            activeStep as PammStep,
        );
    };

    const stepsRenderers = isPsychiatrySession ? stepsRenderersPamm : stepsRenderersCat;

    const showBeforeYouLeave =
        activeStep ===
        (isPsychiatrySession ? PammStep.BeforeYouLeaveStep : Step.BeforeYouLeaveStep);

    const showThankYou =
        activeStep === (isPsychiatrySession ? PammStep.ThankYouStep : Step.ThankYouStep);

    const showLowRating = !isPsychiatrySession && activeStep === Step.LowRatingStep;

    return (
        <OuterWrapper activeStep={activeStep} isPsychiatry={isPsychiatrySession}>
            <Wrapper width={size.width}>
                {!isPsychiatrySession && activeStep === Step.ThankYouStep ? null : (
                    <ButtonsWrapper>
                        <Button
                            onClick={goToThePreviousStep}
                            visible={!hideBackButton}
                            data-testid={RateVideoSessionPageTestID.BackButton}
                        >
                            <ButtonImage src={backIcon} />
                        </Button>

                        <Button
                            onClick={handleCloseButtonPress}
                            data-testid={RateVideoSessionPageTestID.CloseButton}
                            visible={!hideCloseButton}
                        >
                            <ButtonImage src={closeIcon} />
                        </Button>
                    </ButtonsWrapper>
                )}

                {showBeforeYouLeave && (
                    <RateSessionStepWrapper
                        {...rateSessionStepWrapperProps}
                        visibleFor={
                            isPsychiatrySession
                                ? PammStep.BeforeYouLeaveStep
                                : Step.BeforeYouLeaveStep
                        }
                    >
                        <RateSessionBeforeYouLeaveStep
                            onNotNowClick={handleNotNowPress}
                            onSubmitClick={handleSubmit}
                            loading={loading}
                        />
                    </RateSessionStepWrapper>
                )}
                {showThankYou && (
                    <RateSessionStepWrapper
                        {...rateSessionStepWrapperProps}
                        visibleFor={isPsychiatrySession ? PammStep.ThankYouStep : Step.ThankYouStep}
                        fullWidth
                    >
                        <RateSessionThankYouStep
                            providerFirstName={provider.firstName as string}
                            shouldShowScheduling={!!shouldShowScheduling}
                            isPamm={isPsychiatrySession}
                        />
                    </RateSessionStepWrapper>
                )}

                {showLowRating && (
                    <RateSessionStepWrapper
                        {...rateSessionStepWrapperProps}
                        visibleFor={Step.LowRatingStep}
                        innerWrapperMaxWidth={630}
                    >
                        <SessionLowRatingStep provider={provider} />
                    </RateSessionStepWrapper>
                )}

                {shouldUseStepsRenderers() && (
                    <StepsWrapper stepIndex={currentStepIndex}>
                        {steps.map(step => {
                            const key = `rate-step-${
                                isPsychiatrySession ? 'pamm' : 'provider'
                            }-${step}`;

                            return (
                                <React.Fragment key={key}>
                                    {stepsRenderers[step]?.()}
                                </React.Fragment>
                            );
                        })}
                    </StepsWrapper>
                )}
            </Wrapper>
        </OuterWrapper>
    );
};
